import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  HostListener,
  Inject,
  ViewChild,
  ElementRef,
  PLATFORM_ID,
  ChangeDetectorRef
} from '@angular/core';
import { load } from '@2gis/mapgl';
import { GeoJsonSource, Map, MapPointerEvent, Marker, MarkerOptions, Polygon, Polyline } from '@2gis/mapgl/global';
import {
  GeoDataInfo,
  MarkerOptionsWithData,
  MarkerWithData,
  SelectedMarkerParams,
  SelectedPolygon,
  SelectedPolyline,
  SpiralAndStarObject
} from '@shared/components/map/models/map';
import { Select, Store } from '@ngxs/store';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { debounceTime, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { convertCatalogItemFieldsVMToView } from '@utils/converters';
import { CatalogType, CatalogView } from '@shared/models/shared-catalog.state';
import {
  CancelGetGeoDataCluster,
  GetCatalogGeoData,
  GetGeoDataArea,
  GetGeoDataCluster,
  GetGeoDataDistrict
} from '@shared/state/catalog/shared-catalog.action';
import { DOCUMENT, isPlatformServer, Location } from '@angular/common';
import { CatalogDynamicColumns, CatalogItemsQueryParams } from 'app/modules/data/modules/catalog-items-data/models/catalog-item-component';
import { CatalogItemValue, GeoData } from 'app/modules/data/models/catalog';
import {
  ChangedCatalogItemsFilter,
  ChangedCatalogItemsIsNextToMeData,
  ChangedCatalogItemsIsRecommendation
} from 'app/modules/data/modules/catalog-items-data/store/catalog-items.actions';
import { CustomIcons } from '@shared/models/custom-icons';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AuthState } from '@auth/store/auth/auth.state';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { GetRecommendationCatalogItemsForUser } from '@shared/state/shared/shared.action';
import { Router } from '@angular/router';
import { setLocationQueryParams } from '@utils/other-utils';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AddressService } from '@shared/components/map/services/address.service';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { cloneDeep } from 'lodash';
import { TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { CatalogFilter } from '@shared/components/filter-catalog-items/models';
import { GeoDataAreaResponse, GeoDataCluster, GeoDataClusterParams, GeoDataDistrictResponse } from '@shared/models/geodata.service';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { FeatureCollection } from 'geojson';
import { maxValidator } from '@utils/form-validators';
import { ReleaseStatus } from '@shared/models/catalog.service';
import { CatalogItemsState } from '../../../../../data/modules/catalog-items-data/store/catalog-items-state';
import { NextToMeData } from '../../../../../data/modules/catalog-items-data/models/catalog-item.service';
import { SelectCatalogItemShared } from '../../../../../data/store/catalog/catalog.action';
import { CatalogState } from '../../../../../data/store/catalog/catalog.state';
import { ConnectedPosition } from "@angular/cdk/overlay";

const defaultZoom = 10;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class MapComponent implements OnInit, OnDestroy {
  private _myLocationMarker: Marker;
  private _minZoomAreaPoligon = 9;
  private _maxZoomAreaPoligon = 12;
  private _minZoomDistrictPoligon = 12;
  private _maxZoomDistrictPoligon = 15;
  private _prevSelectCatalog: CatalogView;
  private _layerAdd = false;
  private _hasMapFilter: boolean | undefined = false;
  private _prevCenter: number[];
  private _clusterPolygons: Polygon[] = [];
  private _lastFilterValue = '';
  private _isNextMeDataNote: boolean | undefined = undefined;
  private _lastSelectedMarker: MarkerWithData | null;
  private _mapGeoData: GeoDataInfo[];
  private _hasBackObject = false;
  private _clusterMapGeoData: GeoDataCluster | undefined;
  private _selectedObjectId: number | null = null;
  private _selectedObjectGeoData: GeoData | null = null;
  private _selectedObjectParams: SelectedMarkerParams;
  private _allMarkers: MarkerWithData[] = [];
  private _allClusterMarkers: MarkerWithData[] = [];
  private _allPolylines: Polyline[] = [];
  private _allPolygons: Polygon[] = [];
  private _mapgl: typeof mapgl;
  private _areaGeojsonSource: GeoJsonSource;
  private _disctrictGeojsonSource: GeoJsonSource;
  private _notLoadingData = false;
  private _selectedPolyline: SelectedPolyline | null = null;
  private _selectedPolygon: SelectedPolygon | null = null;
  private _spiralAndStarObject: SpiralAndStarObject = {
    markers: [],
    lines: []
  };
  private _featureCollectionClusterArea: FeatureCollection = {
    type: 'FeatureCollection',
    features: []
  };
  private _featureCollectionClusterDistrict: FeatureCollection = {
    type: 'FeatureCollection',
    features: []
  };
  private runChangeClusterData$ = new Subject<{
    bounds: {
      northEast: [number, number];
      northWest: [number, number];
      southEast: [number, number];
      southWest: [number, number];
    } | null;
    hasChangeData: boolean;
  }>();
  private clusterDataChanged$ = new Subject<void>();

  public mapRotation = 0;
  @Select(actionsExecuting([GetGeoDataCluster, GetCatalogGeoData])) public clusterLoading$: Observable<ActionsExecuting>;
  public filterOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    }
  ];
  public componentDestroy: () => Subject<any>;
  public map: Map;
  public showMyPosition = false;
  public isFullscreen = false;
  public icons = CustomIcons;
  public allColumns$: Observable<CatalogDynamicColumns[]>;
  public moscowCenter: number[] = [37.6156, 55.7522];
  public centerMap: number[] = [37.6156, 55.7522];
  public clickRecommendation$: Observable<any>;
  public geoDataArea$: Observable<GeoDataAreaResponse | undefined>;
  public geoDistrictArea$: Observable<GeoDataDistrictResponse | undefined>;
  public maxMapZoom = 19;
  public isNotPublicCatalog = false;

  public set selectedObjectId(value: number | null) {
    this._selectedObjectId = value;
    this.redrawAllPolygons(null);
    if (value === null && this._lastSelectedMarker) {
      this._lastSelectedMarker.setIcon({
        icon: this.pointIcon,
        size: [16.1, 16.1]
      });
    }
  }
  public nextMeData: NextToMeData = {
    isNextMeData: false
  };
  public form: UntypedFormGroup = new UntypedFormGroup({
    radius: new UntypedFormControl({ value: 2000, disabled: !this.nextMeData.isNextMeData }, [maxValidator(5000, this.snackBar)]),
    isNextMeData: new UntypedFormControl(false)
  });

  public radius = this.form.get('radius');

  public get mapGeoData(): GeoDataInfo[] {
    return this._mapGeoData;
  }

  @Input()
  public set mapGeoData(value: GeoDataInfo[] | null) {
    if (this.router.url.includes('map')) {
      const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
      if (selectCatalog) {
        const isRecommendationData =
          this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsRecommendationData).call(this, selectCatalog.id) ?? false;

        const nextToMyData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsNextToMeData).call(this, selectCatalog.id);

        const criteria = this.store.selectSnapshot(CatalogItemsState.getCatalogItemCriteria).call(this, selectCatalog.id);
        if (!isRecommendationData && !nextToMyData?.isNextMeData && criteria.length === 0) {
          return;
        }
      }
    }

    let newValue: GeoDataInfo[] = [];
    if (value) {
      newValue = value;
    }
    if (newValue !== this._mapGeoData) {
      this._mapGeoData = newValue;
      if (this.map && !this.nextMeData.isNextMeData) {
        this.map.setZoom(this.map.getZoom() || defaultZoom);
      }

      this.changeMapState();
    }
  }

  @Input() public onePoint = false;
  @Input() public controlRight = false;
  @Input() public isImpaired = false;
  @Input() public isRecommendationData: boolean;
  @Input() public set defaultNextMyData(value: NextToMeData) {
    if (value) {
      this.form.get('isNextMeData')?.setValue(value.isNextMeData, { emitEvent: false });
      this.form.get('radius')?.setValue(value.radius, { emitEvent: false });
      if (value.isNextMeData) {
        this.form.get('radius')?.enable();
      }
      this.nextMeData = value;
    }
  }
  @Input() public dynamicDataSet: boolean;
  @Input() public showDynamicInfo = false;
  @Input() public syncTableFilter = false;
  @Input() public pointIcon = '/assets/svg/point_map.svg';
  @Input() public pointIconSize = [16, 16];
  @Input() public mapElementId = 'map-container';
  @Input() public showZoomControl = false;
  @Input() public showFullScreenButton = true;
  @Input() public showFilterMenu = false;
  @Input() public clustered = false;
  @Input() public isRecommendationDisabled = false;
  @Output() public readonly clickOnMapEvent = new EventEmitter<number[]>();
  @Output() public readonly clickOnObject = new EventEmitter<number | null>();
  @Output() public readonly clickOnRecommendation = new EventEmitter<boolean>();
  @Output() public readonly clickOnNextMeData = new EventEmitter<NextToMeData>();
  @ViewChild('relativeContainer') public relativeContainerRef: ElementRef<HTMLElement>;

  public breakPointObserve$ = this.breakpointObserver.observe(['(min-width: 1800px)']).pipe(
    tap((state: BreakpointState) => {
      if (state.matches) {
        this._allMarkers.forEach(marker => {
          marker.setIcon({
            icon: this.pointIcon,
            size: [16.1, 16.1]
          });
          marker.setHoverIcon({
            icon: '/assets/svg/location.svg',
            size: [34.1, 34.1]
          });
        });

        if (this._lastSelectedMarker) {
          this._lastSelectedMarker.setIcon({
            icon: '/assets/svg/location.svg',
            size: [34.1, 34.1]
          });
        }
      }
    })
  );
  changeZoom$ = new Subject<number>();

  constructor(
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
    private addressService: AddressService,
    private breakpointObserver: BreakpointObserver,
    @Inject(PLATFORM_ID) private platformId: string,
    private dialog: MatDialog,
    private router: Router,
    private location: Location,
    private snackBar: SnackBarService,
    private cdr: ChangeDetectorRef
  ) {}

  private nextToMeSnackBar(): void {
    this.snackBar.open(
      MirSnackbarType.Info,
      [
        {
          message: 'SNACK_BAR_MESSAGE.ERROR_FILTER_NEXT_TO_ME',
          translate: true
        }
      ],
      5000
    );
  }

  private refusalNextToMeData(): boolean {
    if (this._isNextMeDataNote === false) {
      if (this.form.get('isNextMeData')?.value) {
        this.form.get('isNextMeData')?.setValue(false);
      }
      this.clickOnNextMeData.emit({
        isNextMeData: false
      });
      this.form.controls.radius.disable();
      this.nextToMeSnackBar();
      return true;
    }
    return false;
  }

  private clickOnMap(e: MapPointerEvent): void {
    this.removeAllObjectFromMap();
    this.addMarker(e.lngLat, null, true);
    this.clickOnMapEvent.emit(e.lngLat);
  }

  private removePrevSpiralOrStar(clusterId: number | null = null): void {
    if (this._spiralAndStarObject.cluster) {
      this._spiralAndStarObject.markers.forEach(m => {
        m.destroy();
      });

      this._spiralAndStarObject.markers.forEach(marker => {
        const allMarkerIndex = this._allMarkers.findIndex(x => x.userData?.objectId === marker.userData?.objectId);
        if (allMarkerIndex !== -1) {
          const allMarker = this._allMarkers.splice(allMarkerIndex, 1);
          if (allMarker[0]) {
            allMarker[0].destroy();
          }
        }
      });
      this._spiralAndStarObject.markers = [];
      this._spiralAndStarObject.lines.forEach(m => {
        m.destroy();
      });

      this._spiralAndStarObject.lines = [];
      if (clusterId === null || clusterId !== this._spiralAndStarObject.cluster.id) {
        this.addCluster(
          this._spiralAndStarObject.cluster.coordinate,
          this._spiralAndStarObject.cluster.amount,
          this._spiralAndStarObject.cluster.id,
          this._spiralAndStarObject.cluster.objectIds
        );
      }
      this._spiralAndStarObject.cluster = undefined;
    }
  }

  private clusterClickOnMap(e: MapPointerEvent): void {
    this.removePrevSpiralOrStar();
    if (this._clusterPolygons.length > 0) {
      const currentZoom = this.map.getZoom();
      if (currentZoom < this._maxZoomAreaPoligon) {
        this.map.setZoom(this._maxZoomAreaPoligon);
      } else if (currentZoom < this._maxZoomDistrictPoligon) {
        if (currentZoom >= this._maxZoomAreaPoligon && currentZoom < this._maxZoomDistrictPoligon) {
          this.map.setZoom(this._maxZoomDistrictPoligon);
        } else {
          this.map.setZoom(currentZoom + 1);
        }
      }

      this.centerMap = e.lngLat;
      this.map.setCenter(e.lngLat);
    }
  }

  private markerOptions(
    coordinate: number[],
    userData: { objectId: number | null },
    isMarked: boolean = false
  ): MarkerOptionsWithData {
    const isMobile = this.breakpointObserver.isMatched('800px');
    return {
      coordinates: coordinate,
      icon: isMarked ? '/assets/svg/location.svg' : this.pointIcon,
      size: isMobile ? this.pointIconSize : (isMarked ? [34, 34] : [20, 20]),
      hoverIcon: '/assets/svg/location.svg',
      hoverSize: [34, 34],
      userData: userData,
      zIndex: 101
    };
  }

  private closeCard(): void {
    this._selectedObjectId = null;
    this.setDefaultPolylineStyle();
    this.redrawAllPolygons(null);
    this.clickOnObject.emit(null);
  }

  private clusterMarkerOptions(coordinate: number[], amount: number): MarkerOptions {
    let size = [30, 30];
    if (amount.toString().length > 6) {
      size = [90, 90];
    } else if (amount.toString().length > 5) {
      size = [80, 80];
    } else if (amount.toString().length > 4) {
      size = [70, 70];
    } else if (amount.toString().length > 3) {
      size = [60, 60];
    } else if (amount.toString().length > 2) {
      size = [50, 50];
    } else if (amount.toString().length > 1) {
      size = [40, 40];
    }

    return {
      coordinates: coordinate,
      label: {
        text: amount > 0 ? amount.toString() : '',
        offset: [0, 0],
        relativeAnchor: [0.5, 0.5]
      },
      icon: amount > 0 ? this.pointIcon : '/assets/svg/point_map_opacity.svg',
      size: size,
      zIndex: 100
    };
  }

  private addClickMapMarker(obj: MarkerWithData | Polygon | Polyline, objectId: number | null, coordinate: number[], isSpiralOrStart = false): void {
    if (this.clickOnObject.observed && objectId) {
      obj.on('click', () => {
        this._selectedObjectGeoData = null;
        if (!this._spiralAndStarObject.markers.find(x => x.userData?.objectId === objectId)) {
          this.removePrevSpiralOrStar();
        }
        this._selectedObjectId = null;
        this._selectedPolygon = null;
        this._selectedPolyline = null;

        this.redrawGeoObjects(objectId);
        if (!isSpiralOrStart) {
          this.centerMap = coordinate;
          this.map.setCenter(coordinate);
          this._notLoadingData = true;
          // this.map.setZoom(17.5);
          setTimeout(() => {
            this._notLoadingData = false;
          }, 1000);
        }
      });
    }
  }

  private destroyPolyline(obj: Polyline): void {
    const lineIndex = this._allPolylines.findIndex(x => x === obj);
    if (lineIndex !== -1) {
      this._allPolylines.splice(lineIndex, 1);
      obj.destroy();
    }
  }

  private setDefaultPolylineStyle(): void {
    if (this._selectedPolyline?.obj) {
      this.destroyPolyline(this._selectedPolyline.obj);
      this.addPolyline(
        {
          coordinates: this._selectedPolyline.coordinates,
          zIndex: this._selectedPolyline.zIndex,
          objectId: this._selectedPolyline.objectId,
          color: '#3C5B9C'
        },
        this._selectedPolyline.objectId ?? null
      );
      this._selectedPolyline = null;
    }
  }

  private addClickPolylineObject(obj: Polyline, options: SelectedPolyline, objectId: number | null): void {
    if (this.clickOnObject.observed && objectId) {
      obj.on('click', () => {
        this._selectedObjectGeoData = null;
        if (this._selectedPolyline && this._selectedPolyline?.objectId === objectId) {
          return;
        }
        this._selectedPolyline = {
          color: options.color,
          zIndex: options.zIndex,
          coordinates: options.coordinates,
          objectId: objectId,
        };
        if (this._selectedPolygon?.objectId !== objectId) {
          this._selectedPolygon = null;
        }

        this.redrawGeoObjects(objectId);
      });
    }
  }

  redrawGeoObjects(objectId: number | null): void {
    // this.removePrevSpiralOrStar();
    this.redrawAllPolylines(objectId);
    this.redrawAllPolygons(objectId);
    this.redrawAllMarkers(objectId);
    this.selectMapObject(objectId);
  }

  redrawAllMarkers(objectId: number | null): void {
    const markersCopy = [...this._allMarkers];

    this._allMarkers.forEach(pol => {
      // if (pol.userData) {
        pol.destroy();
      // }
    });
    this._allMarkers = [];

    markersCopy
      // .filter(x => x.userData)
      .forEach(marker => {
        this.addMarker(
          marker.getCoordinates(),
          marker.userData?.objectId ?? null,
          false,
          marker.userData?.objectId === objectId,
        );
      });
  }

  redrawAllPolylines(objectId: number | null): void {
    const polylinesCopy = [...this._allPolylines];

    this._allPolylines.forEach(pol => {
      pol.destroy();
    });
    this._allPolylines = [];

    polylinesCopy
      .map(x => x.userData)
      .forEach(data => {
        this.addPolyline(
          {
            coordinates: data.coordinates,
            zIndex: data.zIndex,
            objectId: data.objectId,
            color: data.objectId === objectId ? '#0088FF' : '#3C5B9C',
            ...(data?.width ? { width: data.width } : {}),
          },
          data?.objectId ?? null
        );
      });
  }

  // перерисовка всех полигонов
  redrawAllPolygons(objectId: number | null): void {
    const polygonCopy = [...this._allPolygons];

    this._allPolygons.forEach(pol => {
      pol.destroy();
    });
    this._allPolygons = [];

    polygonCopy
      .filter(x => x.userData)
      .map(x => x.userData)
      .forEach(data => {
        this.addPolygon(
          {
            coordinates: data.coordinates,
            zIndex: data.zIndex,
            objectId: data.objectId,
            color: data.objectId === objectId ? '#6EB2FF66' : '#3C5B9C2E',
            strokeColor: data.objectId === objectId ? '#0088FF' : '#3C5B9C',
          },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          data?.objectId ?? null
        );
      });
  }

  private addClickPolygonObject(obj: Polygon, options: SelectedPolygon, objectId: number | null): void {
    if (this.clickOnObject.observed && objectId) {
      obj.on('click', () => {
        this._selectedObjectGeoData = null;
        if (this._selectedPolygon && this._selectedPolygon?.objectId === objectId) {
          return;
        }

        this._selectedPolygon = {
          color: options.color,
          zIndex: options.zIndex,
          coordinates: options.coordinates,
          objectId,
          strokeColor: options.strokeColor,
        };
        if (this._selectedPolyline?.objectId !== objectId) {
          this._selectedPolyline = null;
        }
        this.redrawGeoObjects(objectId);
      });
    }
  }

  private selectMapObject(objectId: number | null) {
    const isMobile = this.breakpointObserver.isMatched('800px');
    this._selectedObjectId = objectId;
    this.clickOnObject.emit(objectId);
    if (this._lastSelectedMarker && this._lastSelectedMarker?.userData?.objectId !== objectId) {
      this._lastSelectedMarker.setIcon({
        icon: this.pointIcon,
        size: isMobile ? [16.1, 16.1] : [20, 20]
      });
    }
    if (objectId) {
      const currentMarker = this._allMarkers.find(x => x.userData?.objectId === objectId);
      if (currentMarker) {
        this._lastSelectedMarker = currentMarker;
        currentMarker.setIcon({
          icon: '/assets/svg/location.svg',
          size: [34.1, 34.1]
        });
      }
    } else {
      this._lastSelectedMarker = null;
    }

    if (objectId !== null && this._selectedObjectParams?.objectId !== objectId) {
      setTimeout(() => {
        this._selectedObjectParams = { objectId: objectId } as SelectedMarkerParams;
      }, 1000);
    }
  }

  private addMarker(coordinate: number[], objectId: number | null, isSpiralOrStart = false, isMarked = false): MarkerWithData {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const marker = new this._mapgl.Marker(this.map, this.markerOptions(coordinate, { objectId: objectId }, isMarked)) as MarkerWithData;
    this._allMarkers.push(marker);
    this.addClickMapMarker(marker, objectId, coordinate, isSpiralOrStart);
    return marker;
  }

  private changeFilterAndStateMap(isNextToMyData: boolean | null = null, criteria: string | null = null, isRecomendation: boolean | null = null) {
    const isNewNextToMyData = isNextToMyData ?? this.nextMeData.isNextMeData;

    const newCriteria = criteria ?? this._lastFilterValue;

    const isNewRecomendation = isRecomendation ?? this.isRecommendationData;

    this._hasMapFilter = newCriteria !== '' || isNewNextToMyData || isNewRecomendation;
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    this.clustered = !this._hasMapFilter && selectCatalog.cluster;

    this.closeCard();

    this.drawAreaAndDistrict();
    const bounds = this.map.getBounds();
    setTimeout(() => {
      this.runChangeClusterData$.next({
        bounds: {
          northEast: bounds.northEast as [number, number],
          southWest: bounds.southWest as [number, number],
          southEast: [bounds.northEast[0], bounds.southWest[1]],
          northWest: [bounds.southWest[0], bounds.northEast[1]]
        },
        hasChangeData:true
      });
    });
  }

  private addSpiralOrStar(clusterId: number, marker: MarkerWithData, amount: number, objectIds: number[]) {
    this.removePrevSpiralOrStar(clusterId);

    const coordinate = marker.getCoordinates();
    this._spiralAndStarObject.markers.push(this.addCluster(coordinate, 0, clusterId));

    this._spiralAndStarObject.cluster = {
      id: clusterId,
      amount: amount,
      coordinate: coordinate,
      objectIds: objectIds
    };

    this._spiralAndStarObject.markers.push();
    marker.destroy();
    this._notLoadingData = true;
    this.map.setZoom(this.maxMapZoom);
    this.centerMap = coordinate;
    this.map.setCenter(coordinate);
    // eslint-disable-next-line no-param-reassign
    objectIds = [...objectIds].sort((a, b) => (a > b ? 1 : -1));
    if (amount > 20) {
      this.drawSpiralMarker(coordinate, objectIds);
    } else {
      this.drawStar(coordinate, objectIds);
    }
    return objectIds;
  }

  private addCluster(coordinate: number[], amount: number, clusterId: number, objectIds: number[] = []): MarkerWithData {
    const marker = new this._mapgl.Marker(this.map, this.clusterMarkerOptions(coordinate, amount)) as MarkerWithData;
    marker.on('click', e => {
      this._selectedObjectGeoData = null;
      const currentZoom = this.map.getZoom();
      if (currentZoom < this._maxZoomAreaPoligon) {
        if (this.clustered) {
          this.map.setZoom(this._maxZoomAreaPoligon);
        } else {
          this.map.setZoom(currentZoom + 1);
        }

        this.centerMap = e.lngLat;
        this.map.setCenter(e.lngLat);
      } else if (currentZoom < this._maxZoomDistrictPoligon) {
        if (currentZoom >= this._maxZoomAreaPoligon && currentZoom < this._maxZoomDistrictPoligon && this.clustered) {
          this.map.setZoom(this._maxZoomDistrictPoligon);
        } else if (currentZoom >= 12 && currentZoom < 14 && !this.clustered) {
          this.map.setZoom(15);
        } else {
          this.map.setZoom(currentZoom + 1);
        }
        this.centerMap = e.lngLat;
        this.map.setCenter(e.lngLat);
      } else if (objectIds.length > 0) {
        this.addSpiralOrStar(clusterId, marker, amount, objectIds);
      } else if (currentZoom >= this._maxZoomDistrictPoligon) {
        this.map.setZoom(currentZoom + 1);
        this.centerMap = e.lngLat;
        this.map.setCenter(e.lngLat);
      }
    });
    this._allClusterMarkers.push(marker);

    if (this._selectedObjectParams && objectIds.find(x => x === this._selectedObjectParams.objectId && this._hasBackObject)) {
      this.addSpiralOrStar(clusterId, marker, amount, objectIds);
      this._hasBackObject = false;
    }
    return marker;
  }

  private showMyPositionOnMap(longitude: number, latitude: number) {
    this.centerMap = [longitude, latitude];
    this.map.setCenter(this.centerMap);
    this.map.setZoom(17.5);
    if (this._myLocationMarker) {
      this._myLocationMarker.destroy();
    }
    this._myLocationMarker = new this._mapgl.Marker(this.map, {
      coordinates: this.centerMap,
      icon: '/assets/svg/point_my_location.svg',
      size: [22, 26],
      zIndex: 2
    });
  }

  private addPolyline(options: SelectedPolyline, objectId: number | null): Polyline {
    const line = new this._mapgl.Polyline(
      this.map,
      {
        ...options,
        userData: { ...options, objectId }
      },
    );
    this._allPolylines.push(line);
    this.addClickPolylineObject(
      line,
      {
        coordinates: options.coordinates,
        zIndex: 2,
        color: '#0088FF'
      },
      objectId
    );

    return line;
  }

  private addPolylineForSpiral(coordinatesLine: number[][]): Polyline {
    const options = {
      coordinates: coordinatesLine,
      zIndex: 1,
      color: '#00000c',
      width: 0.1
    };
    const line = new this._mapgl.Polyline(this.map, {
      ...options,
      userData: { ...options, isSpiral: true }
    });
    this._allPolylines.push(line);
    return line;
  }

  private addPolygon(option: SelectedPolygon, objectId: number | null, index: number | null = null): Polygon {
    const polygon = new this._mapgl.Polygon(this.map, {
      ...option,
      userData: {
        ...option
      }
    });
    if (index !== null) {
      this._allPolygons.splice(index, 0, polygon);
    } else {
      this._allPolygons.push(polygon);
    }

    this.addClickPolygonObject(
      polygon,
      {
        ...option,
        color: '#6EB2FF66',
        strokeColor: '#0088FF'
      },
      objectId
    );

    return polygon;
  }

  private drawSpiralMarker(startPoint: number[], objectIds: number[]): void {
    let i = 0;
    const qs = objectIds.length - 1;
    const x0 = startPoint[0];
    const y0 = startPoint[1];
    let phi;
    let x;
    let y;

    const spi = {
      r: 0.0001,
      dr: 0.0001,
      ds: 0.0001,
      phi: 17
    };

    while (i <= qs) {
      phi = Math.atan2(spi.ds, spi.r);
      spi.phi += phi;
      spi.r += (spi.dr * phi) / Math.PI;

      x = x0 + spi.r * Math.sin(spi.phi);
      y = y0 + spi.r * Math.cos(spi.phi);
      this._spiralAndStarObject.lines.push(
        this.addPolylineForSpiral([
          [x, y],
          [x0, y0]
        ])
      );
      this._spiralAndStarObject.markers.push(this.addMarker([x, y], objectIds[i], true));
      i += 1;
    }

    if (this._selectedObjectId && this._spiralAndStarObject.markers.find(x => x.userData?.objectId === this._selectedObjectId)) {
      this.selectMapObject(this._selectedObjectId);
    }

    setTimeout(() => {
      this._notLoadingData = false;
    }, 1000);
  }

  private drawStar(startPoint: number[], objectIds: number[]): void {
    let a = 0;
    const n = objectIds.length;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    let r = 0.00019;
    if (objectIds.length < 7) {
      r = 0.0001;
    }
    const x = startPoint[0];
    const y = startPoint[1];

    let u = 0;
    for (let i = 1; i < n * 2 + 2; i++) {
      if (i % 2 && objectIds[u]) {
        const newX = x + r * Math.cos((a * Math.PI) / 270);
        const newY = y - r * Math.sin((a * Math.PI) / 270);
        this._spiralAndStarObject.markers.push(this.addMarker([newX, newY], objectIds[u], true));
        u += 1;
        this._spiralAndStarObject.lines.push(
          this.addPolylineForSpiral([
            [x, y],
            [newX, newY]
          ])
        );
      }
      a += 270 / n;
    }

    if (this._selectedObjectId && this._spiralAndStarObject.markers.find(x => x.userData?.objectId === this._selectedObjectId)) {
      this.selectMapObject(this._selectedObjectId);
    }

    setTimeout(() => {
      this._notLoadingData = false;
    }, 1000);
  }

  private addAreaPolygon(points: number[][][][], name: string): void {
    points.forEach(p => {
      this._featureCollectionClusterArea.features.push({
        type: 'Feature',
        properties: {
          name: name
        },
        geometry: {
          type: 'Polygon',
          coordinates: p
        }
      });
    });
  }

  private addDistrictPolygon(points: number[][][][], name: string): void {
    points.forEach(p => {
      this._featureCollectionClusterDistrict.features.push({
        type: 'Feature',
        properties: {
          name: name
        },
        geometry: {
          type: 'Polygon',
          coordinates: p
        }
      });
    });
  }

  private addObjectToMap(pointsOptions: GeoData, objectId: number, zIndex: number = 0): void {
    const objectZIndex = zIndex + 2;
    if (!pointsOptions) {
      return;
    }
    switch (pointsOptions.type) {
      case 'GeometryCollection': {
        pointsOptions.geometries?.forEach(pOpt => {
          this.addObjectToMap(pOpt, objectId, objectZIndex);
        });
        break;
      }
      case 'Point': {
        this.addMarker(
          pointsOptions.coordinates as number[],
          objectId,
          true,
          objectId === this._selectedObjectId,
        );
        break;
      }
      case 'MultiPoint': {
        (pointsOptions.coordinates as number[][]).forEach(point => {
          this.addMarker(
            point,
            objectId,
            true,
            objectId === this._selectedObjectId,
          );
        });
        break;
      }
      case 'MultiLineString': {
        (pointsOptions.coordinates as number[][][]).forEach(coordinatesLine => {
          const options = {
            coordinates: coordinatesLine,
            zIndex: objectZIndex,
            color: this._selectedObjectId === objectId ? '#0088FF' : '#3C5B9C'
          };
          this.addPolyline(options, objectId);
        });
        break;
      }
      case 'Polygon': {
        const options = {
          coordinates: pointsOptions.coordinates as number[][][],
          zIndex: objectZIndex,
          color: this._selectedObjectId === objectId ? '#6EB2FF66' : '#3C5B9C2E',
          strokeColor: this._selectedObjectId === objectId ? '#0088FF' : '#3C5B9C',
          objectId,
        };
        this.addPolygon(options, objectId);
        break;
      }
      case 'MultiPolygon': {
        (pointsOptions.coordinates as number[][][][]).forEach(polygonPoints => {
          const options = {
            coordinates: polygonPoints,
            zIndex: objectZIndex,
            color: this._selectedObjectId === objectId ? '#6EB2FF66' : '#3C5B9C2E',
            strokeColor: this._selectedObjectId === objectId ? '#0088FF' : '#3C5B9C',
            objectId,
          };
          this.addPolygon(options, objectId);
        });
        break;
      }
    }
  }

  private changeMapState(): void {
    this.removeAllObjectFromMap();
    this._allClusterMarkers.forEach(marker => marker.destroy());
    this._allClusterMarkers = [];

    if (!this.map || (!this._mapGeoData && !this._clusterMapGeoData)) {
      return;
    }
    if (!this.nextMeData.isNextMeData && !this.isRecommendationData && this._clusterMapGeoData && !this.onePoint && !this._hasMapFilter) {
      if (this._clusterMapGeoData?.objects) {
        this._clusterMapGeoData.objects.forEach(objOpt => {
          this.addObjectToMap(objOpt.geoData, objOpt.globalId);
        });
      }

      if (this._clusterMapGeoData?.clusters) {
        this._clusterMapGeoData.clusters.forEach(cluster => {
          this.addCluster(cluster.coordinatesCluster.coordinates, cluster.amount, cluster.clusterId, cluster?.globalIds ?? []);
        });
      }
    } else if (this._mapGeoData && this._mapGeoData.length > 0) {
      if (this._mapGeoData.length === 1) {
        this.selectMapObject(this._mapGeoData[0].catalogItemId);
        if (this._mapGeoData[0].center) {
          if (this._mapGeoData[0].center.coordinates && this._mapGeoData[0].center.type === 'Point') {
            this.centerMap = this._mapGeoData[0].center.coordinates as number[];
            this.map.setCenter(this._mapGeoData[0].center.coordinates as number[]);
            this.map.setZoom(17);
          }
        } else if (this._mapGeoData[0].allCoordinates?.type === 'Point') {
          this.centerMap = this._mapGeoData[0].allCoordinates.coordinates as number[];
          this.map.setCenter(this._mapGeoData[0].allCoordinates.coordinates as number[]);
          this.map.setZoom(17);
        }

        if (this._mapGeoData.length > 0) {
          if (this._mapGeoData[0].center) {
            this.centerMap = this._mapGeoData[0].center.coordinates as number[];
            this.map.setCenter(this._mapGeoData[0].center.coordinates as number[]);
            this.map.setZoom(17);
          } else if (this._mapGeoData[0].allCoordinates?.type === 'GeometryCollection' && this._mapGeoData[0].allCoordinates.geometries) {
            if (this._mapGeoData[0].allCoordinates.geometries.length > 0) {
              this.setCenter(this._mapGeoData[0].allCoordinates.geometries[0]);
            }
          } else {
            this.setCenter(this._mapGeoData[0].allCoordinates);
          }
        }
      }

      this._mapGeoData.forEach(objOpt => {
        this.addObjectToMap(objOpt.allCoordinates, objOpt.catalogItemId);
      });

      if (this._selectedObjectId && !this._mapGeoData.find(x => x.catalogItemId === this._selectedObjectId)) {
        this.clickOnObject.emit(null);
      } else if (this._selectedObjectId) {
        this.selectMapObject(this._selectedObjectId);
      }
    }

    const params = this.router.parseUrl(this.location.path()).queryParams;

    if (params?.catalogItemGlobalId) {
      this.selectMapObject(+params.catalogItemGlobalId);
    }

    this.selectMapObject(this._selectedObjectId);
  }

  private rotationEnd(): void {
    this.mapRotation = this.map.getRotation();
    this.cdr.detectChanges();
  }

  private setCenter(geoData: GeoData): void {
    if (!geoData) {
      return;
    }
    if (geoData?.type === 'MultiPoint') {
      this.centerMap = (geoData.coordinates as number[][])[0];
      this.map.setCenter((geoData.coordinates as number[][])[0]);
      this.map.setZoom(13.5);
    } else if (geoData.type === 'MultiLineString') {
      this.centerMap = (geoData.coordinates as number[][][])[0][0];
      this.map.setCenter((geoData.coordinates as number[][][])[0][0]);
      this.map.setZoom(13.5);
    } else if (geoData.type === 'MultiPolygon') {
      this.centerMap = (geoData.coordinates as number[][][][])[0][0][0];
      this.map.setCenter((geoData.coordinates as number[][][][])[0][0][0]);
      this.map.setZoom(13.5);
    } else if (geoData.type === 'Polygon') {
      this.centerMap = (geoData.coordinates as number[][][])[0][0];
      this.map.setCenter((geoData.coordinates as number[][][])[0][0]);
      this.map.setZoom(13.5);
    } else if (geoData.type === 'Point') {
      this.map.setCenter(geoData.coordinates as number[]);
      this.map.setZoom(13.5);
    }
  }

  private parseUrl(): void {
    const params = this.router.parseUrl(this.location.path()).queryParams as CatalogItemsQueryParams;
    if (params.filter) {
      this._hasMapFilter = true;
      this.clustered = false;
    }
    if (this.syncTableFilter) {
      const user = this.store.selectSnapshot(AuthState.authenticatedUser);

      const catalogId = this.store.selectSnapshot(SharedCatalogState.selectCatalog).id;
      const nextToMyData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsNextToMeData).call(this, catalogId);
      if (params.filter) {
        this._lastFilterValue = params.filter;
      }

      if (nextToMyData) {
        this.nextMeData = cloneDeep(nextToMyData);
        if (nextToMyData.radius) {
          this.form.controls.radius.setValue(+nextToMyData.radius);
        }
        if (this.nextMeData.isNextMeData) {
          this.form.controls.isNextMeData.setValue(true);
          this.form.controls.radius.enable();
        }
      }

      if (params?.isRecommendationData && user) {
        const isRecommendationDataState = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsRecommendationData).call(this, catalogId);

        if (isRecommendationDataState === undefined) {
          this.isRecommendationData = params?.isRecommendationData.includes('true');
          this.store.dispatch(new ChangedCatalogItemsIsRecommendation(this.isRecommendationData, catalogId, false));
          this.store
            .dispatch(new GetRecommendationCatalogItemsForUser(catalogId))
            .pipe(takeUntil(this.componentDestroy()))
            .subscribe(() => {
              this.store.dispatch(new GetCatalogGeoData(this.nextMeData, this.isRecommendationData, this._lastFilterValue));
            });
        } else {
          this.isRecommendationData = isRecommendationDataState ?? false;
          this.store.dispatch(new ChangedCatalogItemsIsRecommendation(this.isRecommendationData, catalogId, false));
        }
      } else {
        this.isRecommendationData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsRecommendationData).call(this, catalogId) ?? false;
        if (this.isRecommendationData) {
          setLocationQueryParams(
            {
              isRecommendationData: this.isRecommendationData
            },
            this.router,
            this.location
          );
          if (this.isRecommendationData && user) {
            this.store
              .dispatch(new GetRecommendationCatalogItemsForUser(catalogId))
              .pipe(takeUntil(this.componentDestroy()))
              .subscribe(() => {
                this.store.dispatch(new GetCatalogGeoData(this.nextMeData, this.isRecommendationData, this._lastFilterValue));
              });
          }
        }
      }
    }
  }

  private drawAreaAndDistrict(): void {
    if (this.clustered && !this._layerAdd) {
      this.addAreaAndDistrictPoligon();
      if (this._featureCollectionClusterArea.features.length !== 0 && this._featureCollectionClusterDistrict.features.length !== 0) {
        return;
      }
      this.store.dispatch(new GetGeoDataDistrict());
      this.store.dispatch(new GetGeoDataArea());
      this.geoDataArea$ = this.store.select(SharedCatalogState.selectGeoDataArea).pipe(
        tap(res => {
          if (res) {
            res.response.forEach(r => {
              this.addAreaPolygon(r.coordinates.coordinates, r.nameRegEng);
            });

            this._areaGeojsonSource = new this._mapgl.GeoJsonSource(this.map, {
              data: this._featureCollectionClusterArea,
              attributes: {
                foo: 'bar'
              }
            });
          }
        })
      );

      this.geoDistrictArea$ = this.store.select(SharedCatalogState.selectGeoDataDistrict).pipe(
        tap(res => {
          if (res) {
            res.response.forEach(r => {
              this.addDistrictPolygon(r.coordinates.coordinates, r.nameDisRu);
            });

            this._disctrictGeojsonSource = new this._mapgl.GeoJsonSource(this.map, {
              data: this._featureCollectionClusterDistrict,
              attributes: {
                foo1: 'bar1'
              }
            });
          }
        })
      );
      return;
    }
    if (!this.clustered && this.map && this._layerAdd) {
      this._layerAdd = false;
      this.map.removeLayer('area-polygon');
      this.map.removeLayer('district-polygon');
    }
  }

  /**
   * Серверный зум
   * 0-2 - разбивка кластеров по округам
   * 3-4 - разбивка кластеров по районам
   * 5-6 - кластеры без привязки к районам
   * 7 - просто объекты + кластер по множеству точек в одном месте
   * 8 - просто объекты + множество точек в одном месте раскрываются улиткой
   * @param zoom
   * @private
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line consistent-return
  private getServerZoom(zoom: number): number {
    if (zoom >= 9 && zoom < 10) {
      return 0;
    }

    if (zoom >= 10 && zoom < 11) {
      return 1;
    }

    if (zoom >= 11 && zoom < 12) {
      return 2;
    }

    if (zoom >= 12 && zoom < 13) {
      return 3;
    }

    if (zoom >= 13 && zoom < 14) {
      return 4;
    }

    if (zoom >= 14 && zoom < 15) {
      return 4;
    }

    if (zoom >= 15 && zoom < 16) {
      return 5;
    }

    if (zoom >= 16.0 && zoom < 17.0) {
      return 6;
    }

    if (zoom >= 17.0 && zoom < 18.0) {
      return 7;
    }

    if (zoom >= 18.0) {
      return 8;
    }
  }

  private changeClusterData(
    bounds: { northEast: number[]; northWest: number[]; southEast: number[]; southWest: number[] } | null = null,
    hasChangeData = false
  ): void {
    if (this._hasMapFilter || this._notLoadingData || this.isRecommendationData || this.nextMeData.isNextMeData) {
      return;
    }
    this._spiralAndStarObject = {
      markers: [],
      lines: []
    };
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    const zoom = this.getServerZoom(this.map?.getZoom() ?? defaultZoom);

    // eslint-disable-next-line no-console
    console.log('server zoom:', zoom);
    // eslint-disable-next-line no-console
    console.log('map zoom:', this.map?.getZoom());

    // eslint-disable-next-line no-console
    console.log('map center:', this.map?.getCenter());
    const catalogVersionReleaseInfo = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);

    let params: GeoDataClusterParams = {
      idDataset: selectCatalog.datasetId,
      zoom: zoom,
      changeData: hasChangeData,
      coordinates: bounds ?? {
        northEast: [39.48004174836234, 56.15991713406051],
        southWest: [35.75115825153741, 55.3401763826641],
        southEast: [39.48004174836234, 55.3401763826641],
        northWest: [35.75115825153741, 56.15991713406051]
      }
    };

    if (catalogVersionReleaseInfo?.versionReleaseInfo?.releaseDate) {
      params = {
        ...params,
        epoch: catalogVersionReleaseInfo.versionReleaseInfo.releaseDate
      };
    }

    if (selectCatalog) {
      this.store
        .dispatch(new GetGeoDataCluster(params))
        .pipe(take(1), takeUntil(this.componentDestroy()))
        .subscribe(() => {
          this._clusterMapGeoData = this.store.selectSnapshot(SharedCatalogState.clusterGeoData);
          this.changeMapState();
          if (this._selectedObjectGeoData && this._selectedObjectId) {
            this.addObjectToMap(this._selectedObjectGeoData, this._selectedObjectId, 10);
          }
          this.clusterDataChanged$.next();
          // if (this._clusterMapGeoData?.clusters) {
          //   this.redrawGeoObjects(this._selectedObjectId);
          // }
        });
    }
  }

  private zoomEnd() {
    const bounds = this.map.getBounds();
    this.runChangeClusterData$.next({
      bounds: {
        northEast: bounds.northEast as [number, number],
        southWest: bounds.southWest as [number, number],
        southEast: [bounds.northEast[0], bounds.southWest[1]],
        northWest: [bounds.southWest[0], bounds.northEast[1]],
      },
      hasChangeData: false
    });
  }

  private addAreaAndDistrictPoligon(): void {
    if (this.map && !this._layerAdd) {
      //погрешность в математике у 2гиса. Чтобы на 12 выключить слой округов и включить слой районов нужно указать 11.81
      const mathError = 0.19;

      this._layerAdd = true;
      this.map.addLayer({
        id: 'area-polygon',
        type: 'polygon',
        filter: ['match', ['sourceAttr', 'foo'], ['bar'], true, false],
        minzoom: this._minZoomAreaPoligon,
        maxzoom: this._maxZoomAreaPoligon - mathError,
        zIndex: 1,
        style: {
          color: '#13bf694d',
          strokeColor: '#ffffff',
          strokeWidth: 2
        }
      });

      this.map.addLayer({
        id: 'district-polygon',
        type: 'polygon',
        filter: ['match', ['sourceAttr', 'foo1'], ['bar1'], true, false],
        minzoom: this._minZoomDistrictPoligon - mathError,
        maxzoom: this._maxZoomDistrictPoligon - mathError,
        zIndex: 1,
        style: {
          color: '#13bf694d',
          strokeColor: '#ffffff',
          strokeWidth: 2
        }
      });
    }
  }

  private closeAuthModal(): void {
    this.isRecommendationData = false;
    this.clickOnRecommendation.emit(this.isRecommendationData);
    this.changeFilterAndStateMap(null, null, this.isRecommendationData);
    this.cdr.detectChanges();
  }

  private centerEnd() {
    const currentCenter = this.map.getCenter();
    const zoom = this.getServerZoom(this.map?.getZoom() ?? defaultZoom);
    if (Math.abs(this._prevCenter[0] - currentCenter[0]) > 0.001 || (Math.abs(this._prevCenter[1] - currentCenter[1]) > 0.001 && zoom > 1)) {
      this._prevCenter = currentCenter;
      const bounds = this.map.getBounds();
      this.runChangeClusterData$.next({
        bounds: {
          northEast: bounds.northEast as [number, number],
          southWest: bounds.southWest as [number, number],
          southEast: [bounds.northEast[0], bounds.southWest[1]],
          northWest: [bounds.southWest[0], bounds.northEast[1]],
        },
        hasChangeData: false
      });
    }
  }

  public removeAllObjectFromMap(): void {
    [...this._allMarkers, ...this._allPolylines, ...this._allPolygons, ...this._clusterPolygons].forEach(m => {
      m.destroy();
    });
    this._allMarkers = [];
    this._allPolylines = [];
    this._allPolygons = [];
    this._clusterPolygons = [];
  }

  @HostListener('window:resize')
  public resizeMap(): void {
    this.rerenderMap();
  }

  public rerenderMap(): void {
    if (!this.map) {
      return;
    }
    const mapContainer = this.map.getContainer();
    mapContainer.style.width = `${this.relativeContainerRef.nativeElement.offsetWidth}px`;
    mapContainer.style.height = `${this.relativeContainerRef.nativeElement.offsetHeight}px`;
    this.map.invalidateSize();
  }

  public ngOnInit(): void {
    this.parseUrl();
    this.allColumns$ = this.store.select(SharedCatalogState.fields).pipe(
      map(res => {
        if (res) {
          return convertCatalogItemFieldsVMToView([...res].sort((a, b) => (a.order > b.order ? 1 : -1))).filter(
            x => x.type !== CatalogType.File && x.type !== CatalogType.Catalog
          );
        }

        return [];
      })
    );
    if (isPlatformServer(this.platformId)) {
      return;
    }
    load().then(mapglAPI => {
      this._mapgl = mapglAPI;
      this.addNewMap();
    });

    this.form
      .get('isNextMeData')
      ?.valueChanges.pipe(takeUntil(this.componentDestroy()))
      .subscribe(res => {
        if (this._myLocationMarker && !this.showMyPosition) {
          this._myLocationMarker.destroy();
        }

        if (this.refusalNextToMeData()) {
          return;
        }

        const radius = this.form.controls.radius;
        if (res) {
          radius.enable();
          this.getNextMeData();
          this.changeFilterAndStateMap(true, null, null);
        } else {
          radius.disable();
          this.nextMeData = {
            isNextMeData: false,
            radius: this.nextMeData.radius
          };
          this.clickOnNextMeData.emit({
            isNextMeData: false
          });
          this.changeFilterAndStateMap(false, null, null);
          if (this.syncTableFilter) {
            this.store.dispatch(new ChangedCatalogItemsIsNextToMeData(this.nextMeData));
          }
          if (this.isRecommendationData || this._lastFilterValue?.length > 0) {
            this.store.dispatch(new GetCatalogGeoData(this.nextMeData, this.isRecommendationData, this._lastFilterValue));
          } else {
            const bounds = this.map.getBounds();
            this.runChangeClusterData$.next({
              bounds: {
                northEast: bounds.northEast as [number, number],
                southWest: bounds.southWest as [number, number],
                southEast: [bounds.northEast[0], bounds.southWest[1]],
                northWest: [bounds.southWest[0], bounds.northEast[1]],
              },
              hasChangeData: false,
            });
          }
        }
      });

    this.runChangeClusterData$
      .pipe(
        debounceTime(300),
        takeUntil(this.componentDestroy())
      )
      .subscribe(({ bounds, hasChangeData }) => {
        this.changeClusterData(bounds, hasChangeData);
      });

    this.changeZoom$
      .pipe(
        debounceTime(300),
        takeUntil(this.componentDestroy())
      )
      .subscribe(step => {
        this.map.setZoom(this.map.getZoom() + step);
      });
  }

  public addNewMap(): void {
    const mapglAPI = this._mapgl;
    if (this.map) {
      this.map.destroy();
    }
    this.map = new mapglAPI.Map(this.mapElementId, {
      center: this.moscowCenter,
      zoom: defaultZoom,
      key: this.addressService.key,
      zoomControl: false,
      rotation: 0,
      maxZoom: this.maxMapZoom,
      minZoom: 9
    });

    //   new RulerControl(this.map, { position: 'topRight', enabled: true });

    if (this.nextMeData.isNextMeData && this.nextMeData?.longitude && this.nextMeData?.latitude) {
      this.showMyPositionOnMap(this.nextMeData.longitude, this.nextMeData.latitude);
      this.showMyPosition = true;
    }

    this.changeMapState();

    if (this.clickOnMapEvent.observers.length !== 0) {
      this.map.on('click', e => this.clickOnMap(e));
    }

    this.map.on('click', e => this.clusterClickOnMap(e));

    window.addEventListener('resize', () => this.map.invalidateSize());
    this.map.on('rotationend', () => this.rotationEnd());
    if (!this.onePoint) {
      this.map.on('zoomend', () => this.zoomEnd());
      this.map.on('centerend', () => this.centerEnd());

      this.map.on('styleload', () => {
        this.store
          .select(SharedCatalogState.selectCatalog)
          .pipe(takeUntil(this.componentDestroy()))
          .subscribe(res => {
            if (res) {
              this.resetZoom();
              const catalogVersion = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
              this.isNotPublicCatalog = catalogVersion?.versionReleaseInfo?.releaseStatus === ReleaseStatus.New;

              if (this.isNotPublicCatalog) {
                this.form.get('isNextMeData')?.disable();
                this.form.get('isNextMeData')?.disable();
              } else {
                this.form.get('isNextMeData')?.enable();
                this.form.get('isNextMeData')?.enable();
              }

              if (res?.catalogName) {
                if (!this.nextMeData.isNextMeData && !this.isRecommendationData && !this.onePoint && res && res?.id !== this._prevSelectCatalog?.id) {
                  this._prevSelectCatalog = res;
                  if (this._hasMapFilter) {
                    this.clustered = false;
                  } else {
                    this.clustered = res.cluster;
                  }

                  this.drawAreaAndDistrict();
                }

                this.runChangeClusterData$.next({ bounds: null, hasChangeData: true });
              }
              if (this._mapGeoData && this._mapGeoData.length) {
                if (this._mapGeoData[0].allCoordinates?.type === 'Point') {
                  this.map.setCenter(this._mapGeoData[0].allCoordinates.coordinates as number[]);
                  this.map.setZoom(17);
                }
              }
            }
          });
      });
      this.map.on('mouseover', ev => {
        if (this._clusterPolygons.length > 0) {
          this._clusterPolygons.forEach(p => {
            p.destroy();
          });
          this._clusterPolygons = [];
        }
        if (ev.targetData && (ev.targetData as any).feature?.properties?.name) {
          const currentName = (ev.targetData as any).feature.properties.name;

          if ((ev.targetData as any).source === this._areaGeojsonSource) {
            const currentObj = this._featureCollectionClusterArea.features.filter(x => (x as any).properties.name === currentName);
            currentObj.forEach(obj => {
              this._clusterPolygons.push(
                new mapgl.Polygon(this.map, {
                  coordinates: (obj.geometry as any).coordinates,
                  interactive: false,
                  color: '#13bf6999',
                  strokeColor: '#ffffff',
                  strokeWidth: 2
                })
              );
            });
          } else if ((ev.targetData as any).source === this._disctrictGeojsonSource) {
            const currentObj = this._featureCollectionClusterDistrict.features.filter(x => (x as any).properties.name === currentName);
            currentObj.forEach(obj => {
              this._clusterPolygons.push(
                new mapgl.Polygon(this.map, {
                  coordinates: (obj.geometry as any).coordinates,
                  interactive: false,
                  color: '#13bf6999',
                  strokeColor: '#ffffff',
                  strokeWidth: 2
                })
              );
            });
          }
        }
      });
    }
    this._prevCenter = this.map.getCenter();
  }

  public resetZoom(): void {
    this.map.setCenter(this.moscowCenter);
    this.map.setZoom(defaultZoom);
  }

  public setRotationMap(): void {
    this.map.setRotation(0);
  }

  public ngOnDestroy(): void {
    this.store.dispatch(new CancelGetGeoDataCluster());
    if (this.map) {
      this.map?.destroy();
    }
  }

  public getRecommendationClick(isRecommendation: boolean): void {
    this.clickRecommendation$ = this.getRecommendation(isRecommendation).pipe(
      tap(res => {
        if (typeof res === 'boolean') {
          if (!res) {
            this.closeAuthModal();
          } else {
            if (this.syncTableFilter) {
              this.store.dispatch(new ChangedCatalogItemsIsRecommendation(true));
            }

            this.store.dispatch(new GetCatalogGeoData(this.nextMeData, true, this._lastFilterValue));
          }
        }
      })
    );

    this.changeFilterAndStateMap(null, null, isRecommendation);
  }

  public changeFilter($event: CatalogFilter): void {
    this.changeFilterAndStateMap(null, $event.criteria, null);
    if (!$event.isMobile) {
      $event.isRecommendationData = this.isRecommendationData;
      $event.nextMeData = {
        isNextMeData: this.form.get('isNextMeData')?.value,
        radius: this.form.get('radius')?.value
      };
    }

    if (this.syncTableFilter) {
      this.store.dispatch(new ChangedCatalogItemsFilter($event.criteria));

      if ($event?.isRecommendationData !== undefined) {
        this.store.dispatch(new ChangedCatalogItemsIsRecommendation($event?.isRecommendationData));
      }
      if ($event?.nextMeData?.isNextMeData === false) {
        const catalogId = this.store.selectSnapshot(SharedCatalogState.selectCatalog).id;
        const nextToMy = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsNextToMeData).call(this, catalogId);
        this.store.dispatch(
          new ChangedCatalogItemsIsNextToMeData(
            {
              ...(nextToMy ?? {}),
              isNextMeData: false
            },
            catalogId
          )
        );
      }
    } else {
      this.store.dispatch(new SelectCatalogItemShared(null));
    }
    this._lastFilterValue = $event.criteria;

    if ($event.isRecommendationData) {
      this.isRecommendationData = $event.isRecommendationData;
      this.getRecommendation($event.isRecommendationData)
        .pipe(take(1), takeUntil(this.componentDestroy()))
        .subscribe(res => {
          if (typeof res !== 'boolean') {
            if (this.syncTableFilter) {
              this.store.dispatch(new ChangedCatalogItemsIsRecommendation(true));
            }

            if ($event.nextMeData?.isNextMeData) {
              this.form.get('isNextMeData')?.setValue($event.nextMeData.isNextMeData, { emitEvent: false });
              this.form.get('radius')?.setValue($event.nextMeData.radius, { emitEvent: false });
              this.getNextMeData();
            } else {
              this.store.dispatch(new GetCatalogGeoData(this.nextMeData, true, this._lastFilterValue));
            }
          }
        });
    } else if ($event.nextMeData?.isNextMeData) {
      this.form.get('isNextMeData')?.setValue($event.nextMeData.isNextMeData, { emitEvent: false });
      this.form.get('radius')?.setValue($event.nextMeData.radius, { emitEvent: false });
      this.getNextMeData();
    } else {
      this.store.dispatch(
        new GetCatalogGeoData(
          {
            ...this.nextMeData,
            isNextMeData: false
          },
          false,
          this._lastFilterValue
        )
      );
    }
  }

  public getRecommendation(isRecommendationDataParams: boolean | null = null): Observable<any> {
    const user = this.store.selectSnapshot(AuthState.authenticatedUser);
    const catalogId = this.store.selectSnapshot(SharedCatalogState.selectCatalog).id;
    if (!this.isRecommendationData) {
      this.store.dispatch(new ChangedCatalogItemsIsRecommendation(true, catalogId, true));
      const isRecommendationData = true;
      this.clickOnRecommendation.emit(isRecommendationData);
      if (user && catalogId) {
        /* if (isRecommendationDataParams) {
           this.isRecommendationData = isRecommendationDataParams;
        } */
        return this.store.dispatch(new GetRecommendationCatalogItemsForUser(catalogId)).pipe(
          takeUntil(this.componentDestroy()),
          map(() => true)
        );
      }
    } else {
      const isRecommendationData = !!isRecommendationDataParams;
      this.clickOnRecommendation.emit(isRecommendationData);
      if (this.syncTableFilter) {
        this.store.dispatch(new ChangedCatalogItemsIsRecommendation(isRecommendationData));
      }

      this.store.dispatch(new GetCatalogGeoData(this.nextMeData, isRecommendationData, this._lastFilterValue));
      return EMPTY;
    }

    const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
    authorizationDialog.componentInstance.keyOperations = 'GetRecommendation';
    authorizationDialog.componentInstance.operationValue = catalogId;
    return authorizationDialog.afterClosed().pipe(take(1), takeUntil(this.componentDestroy()));
  }

  public getNextMeData(): void {
    const radius = this.form.get('radius');
    if (radius?.value > 5000) {
      radius?.setValue(5000);
    } else if (radius?.value < 100) {
      radius?.setValue(100);
    }
    const window = this.document.defaultView;

    if (this.refusalNextToMeData()) {
      return;
    }
    if (this._isNextMeDataNote === undefined) {
      this.nextToMeSnackBar();
    }

    if (window) {
      // eslint-disable-next-line no-console
      console.log('запрос геоданных');
      window.navigator.geolocation.getCurrentPosition(
        res => {
          this.nextMeData = {
            isNextMeData: true,
            radius: radius?.value,
            latitude: res.coords.latitude,
            longitude: res.coords.longitude
          };

          this.clickOnNextMeData.emit(this.nextMeData);
          if (this.syncTableFilter) {
            this.store.dispatch(new ChangedCatalogItemsIsNextToMeData(this.nextMeData));
          }
          this.store
            .dispatch(new GetCatalogGeoData(this.nextMeData, this.isRecommendationData, this._lastFilterValue))
            .pipe(take(1), takeUntil(this.componentDestroy()))
            .subscribe(() => {
              this.showMyPositionOnMap(res.coords.longitude, res.coords.latitude);
            });
          // eslint-disable-next-line no-console
          console.log('данные получены');
          this._isNextMeDataNote = true;
          this.showMyPosition = true;
        },
        err => {
          this._isNextMeDataNote = false;
          // eslint-disable-next-line no-console
          console.log('данные запрещены');
          // eslint-disable-next-line no-console
          console.log(err);
          this.refusalNextToMeData();
        }
      );
    }
  }

  public toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      const container = this.map.getContainer();
      container.style.width = `${this.document.defaultView?.innerWidth}px`;
      container.style.height = `${this.document.defaultView?.innerHeight}px`;
      this.map.invalidateSize();
    } else {
      setTimeout(() => {
        this.rerenderMap();
      });
    }
  }

  public backSelectedItem(): void {
    if (this._selectedObjectParams) {
      const catalogItem: CatalogItemValue = this.store.selectSnapshot(CatalogState.selectCatalogItem) as CatalogItemValue;
      const geometries = catalogItem?.geoData.geometries;
      const coordinates = catalogItem?.geoData.coordinates;
      this._selectedObjectGeoData = null;

      if (geometries || Array.isArray(coordinates[0])) {
        // прорисовка объекта поверх карты после загрузки кластеризации
        of(null)
          .pipe(
            switchMap(() => this.clusterDataChanged$),
            take(1),
            takeUntil(this.componentDestroy()),
          )
          .subscribe(() => {
            this.addObjectToMap(catalogItem?.geoData, catalogItem?.global_id);
            (catalogItem?.geoData?.geometries || []).forEach((geo: GeoData) => {
              if (geo.type === 'Point') {
                // this.addObjectToMap(geo, catalogItem?.global_id, 10);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                // const marker = new this._mapgl.Marker(
                //   this.map,
                //   this.markerOptions(geo.coordinates as number[], { objectId: catalogItem?.global_id })
                // ) as MarkerWithData;
                // marker.setIcon({
                //   icon: '/assets/svg/location.svg',
                //   size: [34.1, 34.1]
                // });
                // this.addClickMapMarker(marker, catalogItem?.global_id, geo.coordinates as number[]);
              }
            });
          });

        this._selectedObjectGeoData = catalogItem.geoData;
        // allCoordinates должна содержать массив координат в виде [[number; number], [number; number]]
        // далее происходит костыльная обработка для разных уровней вложенности geometries или coordinates
        let allCoordinates: number[][] = [];

        if (geometries) {
          // обработка объектов MultiGeo
          allCoordinates = geometries
            ?.reduce((arr: number[][], geometry: GeoData) => {
              // тут немного грязи из-за вариативной типизации поля coordinates
              switch (geometry.type) {
                case 'Point': {
                  arr.push((geometry.coordinates || []) as number[]);
                  break;
                }
                case 'MultiLineString':
                case 'Polygon': {
                  arr = [...arr, ...(geometry.coordinates as number[][][]).flat()];
                  break;
                }
                case 'MultiPolygon': {
                  arr = [...arr, ...(geometry.coordinates as number[][][][]).flat().flat()];
                  break;
                }
                default: {
                  break;
                }
              }
              return arr;
            }, []);
        } else if (Array.isArray(coordinates[0]?.[0]?.[0])) {
          // обработка объектов MultiPolygon
          allCoordinates = coordinates.flat().flat();
        } else if (Array.isArray(coordinates[0]?.[0])) {
          // обработка объектов Polygon, MultiLineString
          allCoordinates = coordinates.flat();
        } else if (Array.isArray(coordinates[0])) {
          // обработка объектов MultiPoint
          allCoordinates = coordinates;
        }

        const latitudeCoordinates = allCoordinates
          ?.map((coordinates: number[]) => coordinates[1])
          .sort() as number[];
        const longitudeCoordinates = allCoordinates
          ?.map((coordinates: number[]) => coordinates[0])
          .sort() as number[];
        // [longitude, latitude]
        const southWest = [longitudeCoordinates[longitudeCoordinates.length - 1], latitudeCoordinates[0]];
        const northEast = [longitudeCoordinates[0], latitudeCoordinates[latitudeCoordinates.length - 1]];

        this.map.fitBounds(
          { southWest, northEast },
          {
            animation: {
              animate: true, duration: 500
            },
            padding: {
              top: 20, left: 20, bottom: 20, right: 380
            }
          }
        );
      } else {
        // обработка объектов Point
        this.map
          .setCenter(coordinates)
          .setZoom(17.5);
      }
      this._hasBackObject = true;
    }
  }

  public removeSelectedItem(): void {
    this._selectedObjectId = null;
    this._selectedObjectGeoData = null;
    this._selectedPolygon = null;
    this._selectedPolyline = null;
    this.redrawGeoObjects(null);
    this.changeMapState();
    this.clickOnObject.emit(null);
    this.changeFilter({ criteria: '', isMobile: false });
  }

  public setMyLocation(): void {
    if (window && !this.showMyPosition) {
      // eslint-disable-next-line no-console
      console.log('запрос геоданных');
      window.navigator.geolocation.getCurrentPosition(
        res => {
          this.showMyPositionOnMap(res.coords.longitude, res.coords.latitude);
          this.showMyPosition = true;
          // eslint-disable-next-line no-console
          console.log('данные получены');
        },
        err => {
          this.showMyPosition = false;
          // eslint-disable-next-line no-console
          console.log('данные запрещены');
          // eslint-disable-next-line no-console
          console.log(err);
        }
      );
    } else {
      this.showMyPosition = false;
      if (this._myLocationMarker) {
        this._myLocationMarker.destroy();
      }
    }
  }
}
